import 'rc-tree/assets/index.css'
import React, { useCallback, useEffect, useReducer, useRef } from 'react'
import Tree from 'rc-tree'
import { sortBy, omit, uniqBy } from 'lodash'
import { createUseStyles } from 'react-jss'
import useProgressiveDataLoader from '../../hooks/useProgressiveDataLoader'
import {
  GET_ASSET_DEVICE_PROPERTIES_TREE_LOOKUP_QUERY,
} from '../../constants/queries/lookup'
import CircularProgress from "@mui/material/CircularProgress";
import { FormGroup, Input, Label } from "reactstrap";
import upperFirst from 'lodash/upperFirst'


const useStyles = createUseStyles({
  iconContainer: {
    marginTop: '-3px',
  },
  selectAllInput: {
    marginTop: '.1rem'
  }
})


function buildTree(allSites, classes) {
  const _treeData = []
  const _allKeys = []

  for (const site of allSites) {
    const _siteKey = JSON.stringify({ siteId: site.id })
    const siteNode = {
      key: _siteKey,
      title: site.name,
      type: 'SITE',
      children: [],
      checkable: true,
      selectable: false,
    }
    _allKeys.push(_siteKey)

    if (site.assets && Array.isArray(site.assets)) {
      for (const asset of site.assets) {
        const _assetKey = JSON.stringify({ assetId: asset.id })
        const assetNode = {
          key: _assetKey,
          title: asset.name,
          type: 'ASSET',
          children: [],
          checkable: true,
          selectable: false,
        }
        _allKeys.push(_assetKey)

        // Add deviceProperties as children
        if (asset.deviceProperties && Array.isArray(asset.deviceProperties)) {
          for (const dp of asset.deviceProperties) {
            const _dpKey = JSON.stringify({
              assetId: asset.id,
              devicePropertyId: dp.id,
              deviceId: dp.deviceId,
              propertyId: dp.propertyId,
              isHistory: false,
            })
            const uniqKey = JSON.stringify({
              assetId: asset.id,
              deviceId: dp.deviceId,
              propertyId: dp.propertyId,
            })
            assetNode.children.push({
              key: _dpKey,
              uniqKey,
              title: `${dp.deviceName} - ${upperFirst(dp.property?.name || dp.name)}`,
              type: 'DEVICE_PROPERTY',
              isLeaf: true,
              checkable: true,
              selectable: false,
            })
          }
        }


        // Add devicePropertiesHistory as children
        if (asset.devicePropertiesHistory && Array.isArray(asset.devicePropertiesHistory)) {
          for (const dp of asset.devicePropertiesHistory) {
            const _dpKey = JSON.stringify({
              assetId: asset.id,
              devicePropertyId: dp.id,
              deviceId: dp.deviceId,
              propertyId: dp.propertyId,
              isHistory: true,
            })
            const uniqKey = JSON.stringify({
              assetId: asset.id,
              deviceId: dp.deviceId,
              propertyId: dp.propertyId,
            })
            assetNode.children.push({
              key: _dpKey,
              uniqKey,
              title: `${dp.deviceName} - ${upperFirst(dp.property?.name || dp.name)}`,
              type: 'DEVICE_PROPERTY_HISTORY',
              isLeaf: true,
              checkable: true,
              selectable: false,
            })
          }
        }

        // Only add asset node if it has children
        if (assetNode.children.length > 0) {
          assetNode.children = sortBy(uniqBy(assetNode.children, 'uniqKey'), 'title')
          for (const child of assetNode.children) {
            _allKeys.push(child.key)
          }
          siteNode.children.push(assetNode)
        }

      }
    }

    if (siteNode.children.length > 0) {
      _treeData.push(siteNode)
    }
  }
  return { _treeData, _allKeys }
}


function reducer(currentState, newState) {
  return { ...currentState, ...newState }
}

function useAssetDevicePropertiesTreeSelector({ companyId, assetDeviceProperties, setFieldValue, classes }) {
  const treeRef = useRef()

  const { fetchData, data, allDataLoaded } = useProgressiveDataLoader({
    query: GET_ASSET_DEVICE_PROPERTIES_TREE_LOOKUP_QUERY,
    isImmediate: false,
  })

  const [{ treeData, allSites, autoExpandParent, expandedKeys, checkedKeys }, setState] = useReducer(reducer, {
    treeData: [],
    allSites: [],
    autoExpandParent: true,
    expandedKeys: undefined,
    checkedKeys: undefined,
  })

  useEffect(() => {
    if (companyId) {
      fetchData({
        variables: {
          page: 1,
          companyId,
        },
      })
    }
  }, [companyId, fetchData])


  useEffect(() => {
    if (data) {
      const __allSitesMap = new Map();
      const _data = [...data];
      for (const asset of _data) {
        const siteId = asset.site?.id;
        if (siteId) {
          if (!__allSitesMap.has(siteId)) {
            __allSitesMap.set(siteId, {
              ...asset.site,
              assets: [omit(asset, 'site')],
            })
          } else {
            const _siteRef = __allSitesMap.get(siteId)
            _siteRef.assets.push(omit(asset, 'site'))
          }
        }
      }
      for (const [key, value] of __allSitesMap.entries()) {
        __allSitesMap.set(key, { ...value, assets: sortBy(uniqBy(value.assets, 'id'), 'name') })
      }
      const __allSites = sortBy([...__allSitesMap.values()], 'name')
      setState({
        allSites: __allSites,
      })
    }
  }, [data])

  const selectByKeys = useCallback((_checkedKeys) => {
    const assetIds = [];
    const selectedDeviceProperties = [];
    for (const _checkedKey of _checkedKeys) {
      const keyObj = JSON.parse(_checkedKey)
      if (keyObj.devicePropertyId) {
        selectedDeviceProperties.push({
          assetId: keyObj.assetId,
          devicePropertyId: keyObj.devicePropertyId,
          deviceId: keyObj.deviceId,
          propertyId: keyObj.propertyId,
          isHistory: keyObj.isHistory,
        })
        if (!assetIds.includes(keyObj.assetId)) {
          assetIds.push(keyObj.assetId)
        }
      }
    }
    setFieldValue('assetId', assetIds);
    setFieldValue('assetDeviceProperties', selectedDeviceProperties);
    // eslint-disable-next-line
  }, [setFieldValue])

  useEffect(() => {
    if (allSites) {
      const { _treeData, _allKeys } = buildTree(allSites, classes)
      setState({
        treeData: _treeData,
        expandedKeys: _allKeys,
      })
    }
    // eslint-disable-next-line
  }, [allSites, selectByKeys])


  useEffect(() => {
    if (treeData && treeData.length > 0 && assetDeviceProperties && Array.isArray(assetDeviceProperties)) {
      const _checkedKeys = assetDeviceProperties.map(dp =>
        JSON.stringify({
          assetId: dp.assetId,
          devicePropertyId: dp.devicePropertyId,
          deviceId: dp.deviceId,
          propertyId: dp.propertyId,
          isHistory: dp.isHistory,
        })
      )
      setState({
        checkedKeys: _checkedKeys,
      })
    }
  }, [assetDeviceProperties, treeData])

  const onCheck = useCallback((_checkedKeys) => {
    selectByKeys(_checkedKeys)
    // eslint-disable-next-line
  }, [treeData, selectByKeys])

  const onExpand = useCallback((_expandedKeys) => {
    setState({
      expandedKeys: _expandedKeys,
    })
    // eslint-disable-next-line
  }, [])

  const onSelectDeselectAll = useCallback((e) => {
    if (e.target.checked === true) {
      if (treeData && treeData.length > 0) {
        const treemap = new Map();
        treeData.forEach((item => {
          treemap.set(item.key, true);
          if (item.children) {
            item.children.forEach((innerItem => {
              treemap.set(innerItem.key, true);
              if (innerItem.children) {
                innerItem.children.forEach((leafItem => {
                  treemap.set(leafItem.key, true);
                }));
              }
            }));
          }
        }));
        const _checkedKeys = [...treemap.keys()];
        setState({
          checkedKeys: _checkedKeys
        });
        selectByKeys(_checkedKeys)
      }
    } else {
      setState({
        checkedKeys: []
      });
      selectByKeys([])
    }
  }, [treeData, selectByKeys]);

  return {
    treeRef,
    treeData,
    expandedKeys,
    checkedKeys,
    onCheck,
    onExpand,
    autoExpandParent,
    allDataLoaded,
    onSelectDeselectAll
  }

}

const AssetDevicePropertiesTreeSelector = React.memo(function ({ companyId, assetDeviceProperties, setFieldValue, height }) {
  const classes = useStyles()
  const { treeRef, treeData, expandedKeys, checkedKeys, onCheck, onExpand, allDataLoaded, onSelectDeselectAll } = useAssetDevicePropertiesTreeSelector({
    companyId,
    assetDeviceProperties,
    setFieldValue,
    classes,
  })

  if (!allDataLoaded) {
    return <div>
      <CircularProgress size={20} />
    </div>
  }

  return (<div>
    {treeData && treeData.length > 0 && (
      <FormGroup check>
        <Label check>
          <Input onChange={onSelectDeselectAll} type="checkbox" className={classes.selectAllInput} />
          Select/De-Select all
        </Label>
      </FormGroup>
    )}
    <Tree
      ref={treeRef}
      checkable
      multiple
      defaultCheckedAll
      expandedKeys={expandedKeys}
      checkedKeys={checkedKeys}
      treeData={treeData}
      showIcon={false}
      onCheck={onCheck}
      onExpand={onExpand}
      height={height ?? 400}
    />
  </div>)
})


export default AssetDevicePropertiesTreeSelector
